/**
 * Deliverable router (US-030).
 *
 * Manages structured deliverables on a deal: pick one of NIL Go's four
 * activity categories, fill in category-specific fields, attach proof
 * when done. Mirrors the compensation router pattern — scoped to the
 * signed-in athlete, transactional, validators sourced from
 * @app/validators so the form and the boundary never drift.
 *
 * Read access for deliverables stays on `deal.get` (the deal handler
 * already returns them via the orderBy { dueDate: 'asc' } include); this
 * router is write-only plus a per-deliverable `readiness` lookup.
 *
 * The legacy `deal.addDeliverable` + `deal.toggleDeliverable` endpoints
 * stay around for back-compat — older clients still call them — but the
 * new UI exclusively talks to this router.
 *
 * @package @app/api
 */

import {
  activityCategoryEnum,
  deliverableCreateSchema,
  deliverableFieldsSchema,
  deliverableProofSchema,
  deliverableProofTypeEnum,
} from '@app/validators';
import { ORPCError } from '@orpc/server';
import { z } from 'zod';
import { protectedProcedure } from '../middleware/auth';
import {
  computeDeliverableReadiness,
  type DeliverableReadinessResult,
} from '../services/deliverable-readiness';

// CREATE: full validated shape (.superRefine fires).
const createInput = z.object({
  dealId: z.string(),
  deliverable: deliverableCreateSchema,
});

// UPDATE: partial on the raw shape (zod 4 disallows .partial() on a
// refined schema — same constraint US-029 hit with compensation).
const updateInput = z.object({
  id: z.string(),
  patch: deliverableFieldsSchema.partial(),
});

const markCompletedInput = z.object({
  id: z.string(),
  // Exactly one of these must be set — the boundary enforces it.
  proof: deliverableProofSchema.optional(),
  skipProofReason: z.string().min(1).max(500).optional(),
  completedDate: z.string().optional(),
});

const addProofInput = z.object({
  deliverableId: z.string(),
  proof: deliverableProofSchema,
});

async function requireOwnDeliverable(
  // biome-ignore lint/suspicious/noExplicitAny: prisma client type is verbose
  db: any,
  userId: string,
  deliverableId: string,
): Promise<{ dealId: string }> {
  const row = await db.deliverable.findUnique({
    where: { id: deliverableId },
    select: { deal: { select: { athleteId: true, id: true } } },
  });
  if (!row) throw new ORPCError('NOT_FOUND', { message: 'Deliverable not found' });
  if (row.deal.athleteId !== userId) {
    throw new ORPCError('FORBIDDEN', { message: 'Not your deliverable' });
  }
  return { dealId: row.deal.id };
}

async function requireOwnDeal(
  // biome-ignore lint/suspicious/noExplicitAny: prisma client type is verbose
  db: any,
  userId: string,
  dealId: string,
): Promise<void> {
  const deal = await db.deal.findUnique({ where: { id: dealId }, select: { athleteId: true } });
  if (!deal) throw new ORPCError('NOT_FOUND', { message: 'Deal not found' });
  if (deal.athleteId !== userId) {
    throw new ORPCError('FORBIDDEN', { message: 'Not your deal' });
  }
}

export const deliverableRouter = {
  create: protectedProcedure.input(createInput).handler(async ({ context, input }) => {
    const { db, session } = context;
    const userId = session.user.id;
    await requireOwnDeal(db, userId, input.dealId);

    const { createId } = await import('@app/db');
    const d = input.deliverable;

    try {
      const row = await db.deliverable.create({
        data: {
          id: createId(),
          dealId: input.dealId,
          title: d.title,
          description: d.description || null,
          activityCategory: d.activityCategory,
          type: d.type || null,
          platform: d.platform || null,
          dueDate: d.dueDate ? new Date(d.dueDate) : null,
          metadata: d.metadata ? JSON.stringify(d.metadata) : null,
          status: 'PENDING',
        },
      });
      return row;
    } catch (err) {
      if (err instanceof ORPCError) throw err;
      const message = err instanceof Error ? err.message : String(err);
      throw new ORPCError('INTERNAL_SERVER_ERROR', { message: `create failed: ${message}` });
    }
  }),

  update: protectedProcedure.input(updateInput).handler(async ({ context, input }) => {
    const { db, session } = context;
    const userId = session.user.id;
    await requireOwnDeliverable(db, userId, input.id);

    const patch: Record<string, unknown> = {};
    const p = input.patch;
    if (p.title !== undefined) patch.title = p.title;
    if (p.description !== undefined) patch.description = p.description || null;
    if (p.activityCategory !== undefined) patch.activityCategory = p.activityCategory;
    if (p.type !== undefined) patch.type = p.type || null;
    if (p.platform !== undefined) patch.platform = p.platform || null;
    if (p.dueDate !== undefined) patch.dueDate = p.dueDate ? new Date(p.dueDate) : null;
    if (p.metadata !== undefined) patch.metadata = JSON.stringify(p.metadata);

    try {
      const row = await db.deliverable.update({ where: { id: input.id }, data: patch });
      return row;
    } catch (err) {
      if (err instanceof ORPCError) throw err;
      const message = err instanceof Error ? err.message : String(err);
      throw new ORPCError('INTERNAL_SERVER_ERROR', { message: `update failed: ${message}` });
    }
  }),

  /**
   * Mark a deliverable COMPLETED. Requires either a proof attachment OR a
   * non-empty `skipProofReason`. Skip-with-reason is logged via the
   * `skipProofReason` column for the compliance audit trail (US-026).
   */
  markCompleted: protectedProcedure
    .input(markCompletedInput)
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const userId = session.user.id;
      await requireOwnDeliverable(db, userId, input.id);

      if (!input.proof && !input.skipProofReason) {
        throw new ORPCError('BAD_REQUEST', {
          message: 'Attach a proof or provide a reason for skipping it.',
        });
      }

      const { createId } = await import('@app/db');
      const completedDate = input.completedDate ? new Date(input.completedDate) : new Date();

      try {
        // biome-ignore lint/suspicious/noExplicitAny: Prisma.TransactionClient type isn't easily importable here
        return await db.$transaction(async (tx: any) => {
          if (input.proof) {
            await tx.deliverableProof.create({
              data: {
                id: createId(),
                deliverableId: input.id,
                type: input.proof.type,
                url: input.proof.url,
                notes: input.proof.notes || null,
                capturedAt: new Date(),
              },
            });
          }

          const row = await tx.deliverable.update({
            where: { id: input.id },
            data: {
              status: 'COMPLETED',
              completedDate,
              skipProofReason: input.proof ? null : (input.skipProofReason ?? null),
            },
          });
          return row;
        });
      } catch (err) {
        if (err instanceof ORPCError) throw err;
        const message = err instanceof Error ? err.message : String(err);
        throw new ORPCError('INTERNAL_SERVER_ERROR', {
          message: `markCompleted failed: ${message}`,
        });
      }
    }),

  /**
   * Re-open a previously completed deliverable. Doesn't touch existing
   * proofs (they stay attached for history); clears `skipProofReason`.
   */
  reopen: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const userId = session.user.id;
      await requireOwnDeliverable(db, userId, input.id);
      const row = await db.deliverable.update({
        where: { id: input.id },
        data: { status: 'PENDING', completedDate: null, skipProofReason: null },
      });
      return row;
    }),

  addProof: protectedProcedure.input(addProofInput).handler(async ({ context, input }) => {
    const { db, session } = context;
    const userId = session.user.id;
    await requireOwnDeliverable(db, userId, input.deliverableId);

    const { createId } = await import('@app/db');
    try {
      const row = await db.deliverableProof.create({
        data: {
          id: createId(),
          deliverableId: input.deliverableId,
          type: input.proof.type,
          url: input.proof.url,
          notes: input.proof.notes || null,
          capturedAt: new Date(),
        },
      });
      // If the parent had been skip-with-reason'd, attaching a proof
      // supersedes the skip — clear the reason.
      await db.deliverable.updateMany({
        where: { id: input.deliverableId, skipProofReason: { not: null } },
        data: { skipProofReason: null },
      });
      return row;
    } catch (err) {
      if (err instanceof ORPCError) throw err;
      const message = err instanceof Error ? err.message : String(err);
      throw new ORPCError('INTERNAL_SERVER_ERROR', { message: `addProof failed: ${message}` });
    }
  }),

  removeProof: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const userId = session.user.id;
      const existing = await db.deliverableProof.findUnique({
        where: { id: input.id },
        select: { deliverableId: true },
      });
      if (!existing) throw new ORPCError('NOT_FOUND', { message: 'Proof not found' });
      await requireOwnDeliverable(db, userId, existing.deliverableId);

      await db.deliverableProof.delete({ where: { id: input.id } });
      return { ok: true };
    }),

  /**
   * Compute readiness for one deliverable. The deal-level rollup
   * (US-033) sums these up.
   */
  readiness: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ context, input }): Promise<DeliverableReadinessResult> => {
      const { db, session } = context;
      const userId = session.user.id;
      await requireOwnDeliverable(db, userId, input.id);

      const row = await db.deliverable.findUnique({
        where: { id: input.id },
        select: {
          status: true,
          activityCategory: true,
          metadata: true,
          skipProofReason: true,
          _count: { select: { proofs: true } },
        },
      });
      if (!row) throw new ORPCError('NOT_FOUND', { message: 'Deliverable not found' });

      return computeDeliverableReadiness({
        status: row.status,
        activityCategory: row.activityCategory,
        metadata: row.metadata,
        proofCount: row._count.proofs,
        skipProofReason: row.skipProofReason,
      });
    }),
};

// Re-export at the router boundary so the deal router (which still owns
// some deliverable-shaped endpoints for back-compat) can pin shapes.
export { activityCategoryEnum, deliverableProofTypeEnum };
